// Symbol-or-string acceptance — engine-wide SSOT.
//
// Parser shape (see normalize.ts): a bare s-expr symbol kwarg `:k member`
// arrives as `{ $sym: 'member' }`; a quoted string `:k "member"` as a
// plain string. The LLM may write an enum/identifier arg bare or quoted —
// every string/enum *kwarg* of a special form accepts both and normalises
// to the plain string.
//
// This is NOT opt-in. `relaxSymbols` rewrites the string/enum leaves of an
// op schema to symbol-accepting Zod **codecs** (input `string | {$sym}` →
// output the leaf's own type), and `parseOpCall` (errors.ts — the single
// op-call validation chokepoint every dispatch site routes through)
// applies it before `safeParse`. Authors therefore write plain
// `z.string()` / `z.enum(...)`; symbol acceptance can never be forgotten
// or bypassed, and there is no registration wrapper (the engine's
// "Op = schema + registered meta, no defineOp wrapper" invariant holds).
//
// Structural symbol-discriminating slots (`col` Form-1, `join :on`,
// sort-entry) are typed `z.unknown()` / `z.object({ $sym })`, never
// `z.string()` / `z.enum()`, so the visitor leaves them untouched by
// construction — the `{$sym}` wrapper they branch on survives.

import { z } from 'zod';

/** A `{ $sym: string }` node — a bare s-expr symbol after normalize. */
// biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved
const symNode = z.object({ $sym: z.string().min(1) });

/** Codecs produced here, branded so `relaxSymbols` stays idempotent. */
const SYM_CODECS = new WeakSet<object>();

/** Unwrap a bare-symbol node to its name; pass any other value through. */
export const decodeSymbol = (v: unknown): unknown =>
  v !== null && typeof v === 'object' && '$sym' in (v as Record<string, unknown>) ? (v as { $sym: unknown }).$sym : v;

/**
 * Wrap a string/enum leaf so a bare-symbol node `{$sym}` is accepted in
 * addition to the literal form, decoding to the leaf's own output. The
 * leaf schema is reused verbatim as both an input arm and the output
 * schema, so every check it carries (min/max/regex, enum membership) is
 * preserved — the wrapper only widens the accepted input.
 */
const symCodecOf = <SchemaType extends z.ZodType>(leaf: SchemaType): SchemaType => {
  const codec = z.codec(z.union([leaf, symNode]), leaf, {
    decode: decodeSymbol as never,
    encode: (out: unknown) => out as never,
  });
  SYM_CODECS.add(codec);
  return codec as unknown as SchemaType;
};

/** A free identifier kwarg — bare symbol or quoted string. */
export const SymString = symCodecOf(z.string());

/** An enum kwarg — accepts the member as a bare symbol or quoted string. */
export const SymEnum = <const T extends readonly [string, ...string[]]>(values: T): z.ZodType<T[number]> =>
  symCodecOf(z.enum(values)) as unknown as z.ZodType<T[number]>;

type ZodDef = {
  readonly type: string;
  readonly innerType?: z.ZodType;
  readonly element?: z.ZodType;
  readonly items?: readonly z.ZodType[];
  readonly rest?: z.ZodType;
  readonly options?: readonly z.ZodType[];
  readonly shape?: Readonly<Record<string, z.ZodType>>;
  readonly catchall?: z.ZodType;
  readonly checks?: readonly unknown[];
  readonly defaultValue?: unknown;
};

const zdef = (s: unknown): ZodDef | undefined => (s as { _zod?: { def?: ZodDef } })?._zod?.def;

/** Single-inner-type wrappers, by `def.type`: walk the inner, rewrap. */
const REWRAP: Readonly<Record<string, (inner: z.ZodType, d: ZodDef) => z.ZodType>> = {
  optional: (inner) => inner.optional(),
  nullable: (inner) => inner.nullable(),
  default: (inner, d) => inner.default(d.defaultValue as never),
};

// Per-`def.type` rewrite handlers (predicate-table dispatch, mirroring
// `doc/zod-walk.ts`'s `LABEL_BY_TYPE`). A type with no handler — number,
// boolean, literal, unknown, any, null/nan, non-codec pipes such as
// `spanArg`'s `.transform`, AND `union` — is opaque: returned untouched
// (no symbol acceptance). `union` is deliberately opaque: no op kwarg
// needs symbol acceptance inside a union (the structural `join :on`
// unions are object/tuple), and rewriting a union's string/enum options
// to codecs would defeat `engineZodErrorMap`'s union-branch
// introspection (errors.ts SSOT). Every handler returns the *same
// instance* when its subtree has no string/enum leaf, so untouched
// containers keep their checks/modifiers bit-for-bit.
const WALK: Readonly<Record<string, (s: z.ZodType, d: ZodDef) => z.ZodType>> = {
  string: (s) => symCodecOf(s),
  enum: (s) => symCodecOf(s),
  optional: (s, d) => {
    const inner = walk(d.innerType as z.ZodType);
    const rewrap = REWRAP[d.type];
    return inner === d.innerType || rewrap === undefined ? s : rewrap(inner, d);
  },
  array: (s, d) => {
    const el = walk(d.element as z.ZodType);
    return el === d.element ? s : withArrayChecks(z.array(el), d.checks);
  },
  tuple: (s, d) => {
    const items = (d.items ?? []).map((it) => [it, walk(it)] as const);
    const rest = d.rest === undefined ? undefined : walk(d.rest);
    if (items.every(([o, n]) => o === n) && rest === d.rest) {
      return s;
    }
    const tup = z.tuple(items.map(([, n]) => n) as [z.ZodType, ...z.ZodType[]]);
    return rest === undefined ? tup : tup.rest(rest);
  },
  object: (s, d) => {
    const mapped = Object.entries(d.shape ?? {}).map(([k, v]) => [k, walk(v), v] as const);
    if (mapped.every(([, n, o]) => n === o)) {
      return s;
    }
    const obj = z.object(Object.fromEntries(mapped.map(([k, n]) => [k, n])));
    return d.catchall === undefined ? obj : obj.catchall(d.catchall);
  },
};
// `nullable` / `default` share `optional`'s walk-inner-then-rewrap body.
Object.assign(WALK, { nullable: WALK.optional, default: WALK.optional });

/** Reapply length checks lost when an array is rebuilt with a new element. */
function withArrayChecks(arr: z.ZodArray, checks: readonly unknown[] | undefined): z.ZodType {
  return (checks ?? []).reduce<z.ZodType>((acc, c) => {
    const cd = zdef(c) as { check?: string; minimum?: number; maximum?: number; length?: number } | undefined;
    if (cd?.check === 'min_length') {
      return (acc as z.ZodArray).min(cd.minimum as number);
    }
    if (cd?.check === 'max_length') {
      return (acc as z.ZodArray).max(cd.maximum as number);
    }
    if (cd?.check === 'length_equals') {
      return (acc as z.ZodArray).length(cd.length as number);
    }
    return acc;
  }, arr);
}

/**
 * Recursively rewrite the string/enum leaves of `schema` to symbol-
 * accepting codecs. Returns the *same instance* whenever a subtree has
 * no string/enum leaf, so untouched containers keep their checks and
 * modifiers (e.g. `z.array(z.unknown()).min(1)`, the structural
 * `z.object({ $sym })` of `join :on`) bit-for-bit. Number/boolean/
 * literal/unknown leaves and non-codec pipes are returned unchanged.
 */
function walk(s: z.ZodType): z.ZodType {
  if (SYM_CODECS.has(s)) {
    return s;
  }
  const d = zdef(s);
  if (d === undefined) {
    return s;
  }
  const handler = WALK[d.type];
  return handler === undefined ? s : handler(s, d);
}

const RELAX_CACHE = new WeakMap<z.ZodType, z.ZodType>();

/**
 * Memoised entry point: relax an op schema's string/enum kwarg leaves to
 * accept bare symbols. Cached per schema instance (op schemas are
 * module-level constants), so the transform runs once per op.
 */
export const relaxSymbols = (schema: z.ZodType): z.ZodType => {
  const cached = RELAX_CACHE.get(schema);
  if (cached !== undefined) {
    return cached;
  }
  const relaxed = walk(schema);
  RELAX_CACHE.set(schema, relaxed);
  return relaxed;
};
